import { Box, Button, Flex, HStack, Icon, Stack, Text, Tooltip } from '@chakra-ui/react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { InfoIcon } from '@chakra-ui/icons';
import InputComponent from '@components/InputComponent/InputComponent';
import RadioComponent from '@components/RadioComponent/RadioComponent';
// import { CustomFieldOrigin, CustomFieldOriginsArray } from '@utils/enum/CustomFieldOrigin';
import { FC, useCallback, useMemo } from 'react';
import { Control, FieldErrors, FieldValues, UseFormRegister } from 'react-hook-form';
import { CustomerCustomField } from 'types/formData';
import { RadioItem } from 'types/formElement';
import { MdAdd, MdRemove } from 'react-icons/md';
import palette from '@theme/assets/palette';
import UploadComponent from '@components/UploadComponent/UploadComponent';

interface Props {
    id: string;
    customField: CustomerCustomField;
    customIndex: number;
    errors: FieldErrors<FieldValues>;
    isErasable?: boolean;
    isPa?: boolean;
    tooltipText?: string;
    register: UseFormRegister<FieldValues>;
    control: Control<FieldValues>;
    updateCustomField: (customField: CustomerCustomField) => void;
    onEraseField?: () => void;
    onAddField?: () => void;
    onUploadFile: (files: File[]) => void;
    onDeleteFile: () => void;
}

const CustomerCustomFieldElement: FC<Props> = ({
    id,
    customField,
    customIndex,
    errors,
    isErasable = false,
    tooltipText = null,
    isPa = false,
    onEraseField,
    onAddField,
    register,
    control,
    onUploadFile,
    onDeleteFile,
    updateCustomField,
}) => {
    const originRadioOptions: RadioItem[] = useMemo(() => {
        return [
            {
                id: 'P',
                label: 'il cliente ci trasferirà i dati da profilo passeggero con modalità che esulano da questo modulo',
            },
            {
                id: 'A-1',
                label: 'campo libero',
            },
            {
                id: 'A-2',
                label: 'picklist da associare',
            },
        ];
    }, []);

    const booleanRadioOptions: RadioItem[] = useMemo(() => {
        return [
            {
                id: 'true',
                label: 'Sì',
            },
            {
                id: 'false',
                label: 'No',
            },
        ];
    }, []);

    /* const originRadioOptions: RadioItem[] = useMemo(() => {
        return CustomFieldOriginsArray.map((origin: CustomFieldOrigin) => {
            return {
                id: origin,
                label: origin,
            };
        });
    }, []); */

    const handleChangeCustomName = useCallback(
        (value: string) => {
            updateCustomField({
                ...customField,
                customName: value,
            });
        },
        [customField, updateCustomField],
    );

    const handleUpdateRadio = useCallback(
        (
            value: string,
            key: 'mandatory' | 'validityCheck' | 'meaningful' | 'origin' | 'visibleInBilling' | 'dividedInCostCentre',
        ) => {
            if (key === 'origin') {
                updateCustomField({
                    ...customField,
                    [key]: value,
                });
            } else {
                updateCustomField({
                    ...customField,
                    [key]: value === 'true' ? true : false,
                });
            }
        },
        [customField, updateCustomField],
    );

    const customFieldName = useMemo(() => {
        if (customIndex > 0 && !customField.name) {
            // if (customIndex === 4) {
            //     return <Text variant="titleCustomField">Campo dedicato a CIG</Text>;
            // } else if (customIndex === 5) {
            //     return <Text variant="titleCustomField">Campo dedicato a CUP</Text>;
            // }
            return <Text variant="titleCustomField">Campo personalizzato {customIndex}</Text>;
        } else {
            return <Text variant="titleCustomField">{customField.name}</Text>;
        }
    }, [customField, customIndex]);

    const tooltipTextMemo = useMemo(() => {
        if (customIndex === 4) {
            return 'Campo dedicato a CIG';
        }
        if (customIndex === 5) {
            return 'Campo dedicato a CUP';
        }
        return 'In questi campi è possibile inserire informazioni legate alla singola transazione (es. divisione, centro di costo alternativo, sede)';
    }, [customIndex]);

    const maxCharacters = useMemo(() => {
        if (customIndex < 4) {
            return 15;
        }
        return 35;
    }, [customIndex]);

    if (customField.collapsed) {
        return (
            <Button
                // leftIcon={<Icon as={MdAdd} />}
                size="lg"
                variant={'main'}
                bg={palette.brand01}
                onClick={onAddField}
                w="100%"
                isDisabled={(customIndex === 4 || customIndex === 5) && !isPa}
            >
                <Stack direction="row" gap="5px" alignItems={'center'}>
                    <Icon as={MdAdd} />
                    <Text>aggiungi</Text>
                    <Text w="auto">{customFieldName}</Text>
                    {tooltipText !== null ? (
                        <Box position="relative" top={-2}>
                            <Tooltip label={tooltipTextMemo} placement="top">
                                <InfoIcon boxSize={3} />
                            </Tooltip>
                        </Box>
                    ) : null}
                    {customIndex === 4 || customIndex === 5 ? (
                        <Text fontSize="14px" lineHeight="28px">
                            solo pubblica amministrazione
                        </Text>
                    ) : null}
                </Stack>
            </Button>
        );
    }

    return (
        <Stack border="1px solid rgba(0,0,0,0.4)">
            <Stack direction="column" padding="30px" gap="20px" w="100%">
                <HStack as="div" w="100%" alignItems="flex-start">
                    <Text w="auto">{customFieldName}</Text>
                    {tooltipText !== null ? (
                        <Box position="relative" top={-2}>
                            <Tooltip label={tooltipTextMemo} placement="top">
                                <InfoIcon boxSize={3} />
                            </Tooltip>
                        </Box>
                    ) : null}
                    {/* {isErasable ? (
                    <Flex flex={1} justifyContent="flex-end">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                if (onEraseField) onEraseField();
                            }}
                        >
                            X
                        </Button>
                    </Flex>
                ) : null} */}
                    <Flex flex={1} justifyContent="flex-end">
                        <Text
                            textAlign="right"
                            fontSize="12px"
                            lineHeight="14.4px"
                            color="#1A1A1A"
                            opacity="0.5"
                            fontWeight="700"
                        >
                            Max {maxCharacters} caratteri
                        </Text>
                    </Flex>
                </HStack>
                {!customField.name ? (
                    <InputComponent
                        id={`${id}_customName`}
                        label="Nome campo personalizzato"
                        register={register}
                        value={customField.customName ?? ''}
                        onChange={handleChangeCustomName}
                        isInvalid={errors[`${id}_customName`] ?? null}
                        // maxLength={customIndex < 4 ? 15 : 35}
                        // isRequired
                    />
                ) : null}
                <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                    <RadioComponent
                        id={`${id}_mandatory`}
                        selected={customField.mandatory ? 'true' : 'false'}
                        register={register}
                        items={booleanRadioOptions}
                        direction="column"
                        onChange={(value) => handleUpdateRadio(value, 'mandatory')}
                        name="Campo obbligatorio"
                        isRequired
                    />
                    <RadioComponent
                        id={`${id}_validityCheck`}
                        selected={customField.validityCheck ? 'true' : 'false'}
                        register={register}
                        items={booleanRadioOptions}
                        direction="column"
                        onChange={(value) => handleUpdateRadio(value, 'validityCheck')}
                        name="Controllo validità"
                        isRequired
                    />
                    {customIndex != 0 ? (
                        <RadioComponent
                            id={`${id}_meaningful`}
                            selected={customField.meaningful ? 'true' : 'false'}
                            register={register}
                            items={booleanRadioOptions}
                            direction="column"
                            onChange={(value) => handleUpdateRadio(value, 'meaningful')}
                            name="Significativo *"
                            isRequired
                        />
                    ) : (
                        <Stack flex="1 1 100%" />
                    )}
                </Stack>
                <RadioComponent
                    id={`${id}_origin`}
                    selected={customField.origin}
                    register={register}
                    items={originRadioOptions}
                    direction="column"
                    onChange={(value) => handleUpdateRadio(value, 'origin')}
                    name="Origine **"
                    isRequired
                />
                {customField.origin === 'A-2' ? (
                    <UploadComponent
                        id={`${id}_picklistFile`}
                        title={'Carica documento picklist:'}
                        subtitle=""
                        uploadedFile={customField.attachments}
                        updateFile={onUploadFile}
                        deleteFile={onDeleteFile}
                        control={control}
                        maxSize={153600}
                        sizeString="150 KB"
                    />
                ) : null}
                <Stack direction="row" justify="center" align="center" spacing="30px" alignSelf="stretch">
                    <RadioComponent
                        id={`${id}_visibleInBilling`}
                        selected={customField.visibleInBilling ? 'true' : 'false'}
                        register={register}
                        items={booleanRadioOptions}
                        direction="column"
                        onChange={(value) => handleUpdateRadio(value, 'visibleInBilling')}
                        name="Visibile in fattura"
                        isRequired
                        flex={1}
                    />
                    <RadioComponent
                        id={`${id}_dividedInCostCentre`}
                        selected={customField.dividedInCostCentre ? 'true' : 'false'}
                        register={register}
                        items={booleanRadioOptions}
                        direction="column"
                        onChange={(value) => handleUpdateRadio(value, 'dividedInCostCentre')}
                        name="Estratti conto e fatture suddivisi per centri di costo"
                        isRequired
                        flex={1.5}
                    />
                </Stack>
            </Stack>
            {isErasable ? (
                <Button
                    size="lg"
                    variant={'main'}
                    bg={palette.ui04}
                    color={palette.ui05}
                    onClick={onEraseField}
                    w="100%"
                >
                    <Stack direction="row" gap="5px" alignItems={'center'}>
                        <Icon as={MdRemove} />
                        <Text>rimuovi</Text>
                        <Text w="auto">{customFieldName}</Text>
                    </Stack>
                </Button>
            ) : null}
        </Stack>
    );
};

export default CustomerCustomFieldElement;
